import { useDeferredValue, useState, type FormEvent } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { DateTime } from "luxon";
import useUser from "../users/hooks/useUser";
import { TextExportActions } from "./TextExportActions";
import { formatCartText, formatOrderText } from "./orderingTextExport";
import {
  orderingApi,
  type CartLine,
  type CatalogOffer,
  type CreateProductInput,
  type MegadentalInvoiceLine,
  type OrderDetail,
  type OrderStatus,
} from "./orderingApi";

const orderStatusLabels: Record<OrderStatus, string> = {
  ordered: "Commandée",
  partially_received: "Partiellement reçue",
  received: "Reçue",
};

type MegadentalImportLine = MegadentalInvoiceLine & {
  importId: string;
  manufacturerReference: string;
  keywords: string[];
};

function formatPrice(cents: number) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);
}

function formatDate(value: string) {
  return DateTime.fromISO(value).toFormat("dd/MM/yyyy HH:mm");
}

function formatProductLabel(brand: string, commercialName: string) {
  return [brand, commercialName].filter(Boolean).join(" · ");
}

function priceToCents(value: FormDataEntryValue | null) {
  if (typeof value !== "string") return null;
  const normalized = value.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const [euros, decimals = ""] = normalized.split(".");
  const cents = Number(euros) * 100 + Number(`${decimals}00`.slice(0, 2));
  return Number.isSafeInteger(cents) && cents <= 2_147_483_647 ? cents : null;
}

function catalogKey(query: string) {
  return ["ordering-catalog", query];
}

const OrderingPage = () => {
  const user = useUser();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [authorId, setAuthorId] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [productMessage, setProductMessage] = useState<string | null>(null);
  const [isMegadentalImportOpen, setIsMegadentalImportOpen] = useState(false);
  const [isBrandManagementOpen, setIsBrandManagementOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState<CatalogOffer | null>(null);
  const [megadentalImportLines, setMegadentalImportLines] = useState<
    MegadentalImportLine[]
  >([]);
  const [megadentalImportMessage, setMegadentalImportMessage] = useState<
    string | null
  >(null);
  const [megadentalInvoiceNumber, setMegadentalInvoiceNumber] = useState("");
  const [megadentalInvoiceDate, setMegadentalInvoiceDate] = useState(
    DateTime.now().toFormat("yyyy-LL-dd"),
  );
  const [megadentalOrderedAt, setMegadentalOrderedAt] = useState(
    DateTime.now().toFormat("yyyy-LL-dd"),
  );
  const deferredSearch = useDeferredValue(search);
  const catalogSearch = deferredSearch.trim();
  const isCatalogSearchReady = catalogSearch.length >= 3;
  const isAdmin = user?.tag === "doctor" && user.username === "admin";

  const catalogQuery = useQuery({
    queryKey: catalogKey(catalogSearch),
    queryFn: () => orderingApi.searchCatalog(catalogSearch),
    enabled: isCatalogSearchReady,
  });
  const suppliersQuery = useQuery({
    queryKey: ["ordering-suppliers"],
    queryFn: orderingApi.listSuppliers,
  });
  const cartQuery = useQuery({
    queryKey: ["ordering-cart"],
    queryFn: orderingApi.getCart,
    refetchInterval: 10_000,
  });
  const ordersQuery = useInfiniteQuery({
    queryKey: ["ordering-orders", authorId],
    queryFn: ({ pageParam }) =>
      orderingApi.listOrders({
        authorId: authorId || undefined,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (page) => page.nextOffset,
  });
  const orderAuthorsQuery = useQuery({
    queryKey: ["ordering-order-authors"],
    queryFn: orderingApi.listOrderAuthors,
  });
  const selectedOrderQuery = useQuery({
    queryKey: ["ordering-order", selectedOrderId],
    queryFn: () => {
      if (!selectedOrderId)
        throw new Error("Aucune commande n’est sélectionnée.");
      return orderingApi.getOrder(selectedOrderId);
    },
    enabled: Boolean(selectedOrderId),
  });

  const refreshCatalog = async () => {
    await queryClient.invalidateQueries({ queryKey: ["ordering-catalog"] });
  };
  const refreshCart = async () => {
    await queryClient.invalidateQueries({ queryKey: ["ordering-cart"] });
  };
  const refreshOrders = async (orderId?: string) => {
    await queryClient.invalidateQueries({ queryKey: ["ordering-orders"] });
    await queryClient.invalidateQueries({
      queryKey: ["ordering-order-authors"],
    });
    if (orderId) {
      await queryClient.invalidateQueries({
        queryKey: ["ordering-order", orderId],
      });
    }
  };

  const cartMutation = useMutation({
    mutationFn: orderingApi.setCartQuantity,
    onSuccess: refreshCart,
  });
  const checkoutMutation = useMutation({
    mutationFn: orderingApi.createOrdersFromCart,
    onSuccess: async (orders) => {
      setSelectedOrderId(orders[0]?.id ?? null);
      await refreshCart();
      await refreshOrders();
    },
  });
  const createProductMutation = useMutation({
    mutationFn: orderingApi.createProduct,
    onSuccess: async (_offers, input) => {
      await Promise.all([
        refreshCatalog(),
        ...(input.cart ? [refreshCart()] : []),
      ]);
      setIsProductFormOpen(false);
      setProductMessage(
        input.cart
          ? "Référence ajoutée à la liste de courses."
          : "Référence enregistrée dans le catalogue.",
      );
    },
  });
  const openProductForm = () => {
    createProductMutation.reset();
    setProductMessage(null);
    setIsProductFormOpen(true);
  };
  const promoteMutation = useMutation({
    mutationFn: orderingApi.promoteProduct,
    onSuccess: refreshCatalog,
  });
  const updateCatalogMutation = useMutation({
    mutationFn: async (options: {
      productId: string;
      offerId: string;
      brand: string;
      commercialName: string;
      manufacturerReference: string;
      keywords: string[];
      supplierReference: string;
      catalogPriceCents: number;
    }) => {
      await orderingApi.updateProduct(options);
      await orderingApi.updateOffer(options);
    },
    onSuccess: async () => {
      setEditingOffer(null);
      await refreshCatalog();
    },
  });
  const createCatalogKeywordMutation = useMutation({
    mutationFn: orderingApi.createKeyword,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["ordering-catalog-keywords"],
      });
    },
  });
  const createCatalogBrandMutation = useMutation({
    mutationFn: orderingApi.createBrand,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["ordering-catalog-brand-suggestions"],
      });
    },
  });
  const receiptMutation = useMutation({
    mutationFn: orderingApi.createReceipt,
    onSuccess: async (order) => {
      await refreshOrders(order.order.id);
    },
  });
  const previewMegadentalMutation = useMutation({
    mutationFn: orderingApi.previewMegadentalInvoice,
    onSuccess: (preview) => {
      setMegadentalInvoiceNumber(preview.invoiceNumber ?? "");
      setMegadentalInvoiceDate(
        preview.invoiceDate ?? DateTime.now().toFormat("yyyy-LL-dd"),
      );
      setMegadentalOrderedAt(
        preview.orderedAt ?? DateTime.now().toFormat("yyyy-LL-dd"),
      );
      setMegadentalImportLines(
        preview.lines.map((line, index) => ({
          ...line,
          importId: `${line.supplierReference}-${index}`,
          manufacturerReference: "",
          keywords: [],
        })),
      );
    },
  });
  const importMegadentalMutation = useMutation({
    mutationFn: (input: {
      invoiceNumber: string;
      invoiceDate: string;
      orderedAt: string;
      lines: MegadentalImportLine[];
    }) =>
      orderingApi.importMegadentalInvoice({
        ...input,
        lines: input.lines.map(({ importId: _importId, ...line }) => line),
      }),
    onSuccess: async (result) => {
      setIsMegadentalImportOpen(false);
      setMegadentalImportLines([]);
      setMegadentalImportMessage(
        `${result.createdCount} référence${result.createdCount > 1 ? "s" : ""} proposée${result.createdCount > 1 ? "s" : ""}, ${result.linkedCount} référence${result.linkedCount > 1 ? "s" : ""} existante${result.linkedCount > 1 ? "s" : ""} liée${result.linkedCount > 1 ? "s" : ""}.`,
      );
      await refreshCatalog();
      await refreshOrders(result.orderId);
      setSelectedOrderId(result.orderId);
    },
  });

  const cart = cartQuery.data ?? [];
  const quantitiesByOffer = new Map(
    cart.map((line) => [line.offerId, line.quantity]),
  );
  const catalog = catalogQuery.data ?? [];
  const orders = ordersQuery.data?.pages.flatMap((page) => page.orders) ?? [];
  const authors = orderAuthorsQuery.data ?? [];
  const mutationError =
    cartMutation.error ??
    checkoutMutation.error ??
    promoteMutation.error ??
    updateCatalogMutation.error ??
    createCatalogKeywordMutation.error ??
    createCatalogBrandMutation.error ??
    receiptMutation.error ??
    previewMegadentalMutation.error ??
    importMegadentalMutation.error;

  const updateMegadentalImportLine = (
    importId: string,
    field:
      | "brand"
      | "commercialName"
      | "manufacturerReference"
      | "deliveredQuantity"
      | "invoicedUnitPriceCents"
      | "invoicedTotalCents",
    value: string,
  ) => {
    setMegadentalImportLines((lines) =>
      lines.map((line) =>
        line.importId !== importId
          ? line
          : field === "deliveredQuantity"
            ? { ...line, deliveredQuantity: Number(value) }
            : field === "invoicedUnitPriceCents"
              ? { ...line, invoicedUnitPriceCents: priceToCents(value) }
              : field === "invoicedTotalCents"
                ? { ...line, invoicedTotalCents: priceToCents(value) }
                : { ...line, [field]: value },
      ),
    );
  };

  const updateMegadentalImportKeywords = (
    importId: string,
    keywords: string[],
  ) => {
    setMegadentalImportLines((lines) =>
      lines.map((line) =>
        line.importId === importId ? { ...line, keywords } : line,
      ),
    );
  };

  const persistCatalogKeyword = async (name: string) => {
    await createCatalogKeywordMutation.mutateAsync(name);
  };
  const persistCatalogBrand = async (name: string) => {
    const brand = await createCatalogBrandMutation.mutateAsync(name);
    return brand.name;
  };

  const changeQuantity = (
    offerId: string,
    currentQuantity: number,
    delta: number,
  ) => {
    const quantity = Math.max(0, currentQuantity + delta);
    cartMutation.mutate({ offerId, quantity });
  };

  const submitProduct = (product: CreateProductInput) => {
    createProductMutation.mutate({
      catalogStatus: isAdmin ? "master" : "proposed",
      ...product,
    });
  };

  const submitCatalogEdit = (
    event: FormEvent<HTMLFormElement>,
    keywords: string[],
  ) => {
    event.preventDefault();
    if (!editingOffer) return;
    const formData = new FormData(event.currentTarget);
    const brand = formData.get("brand");
    const commercialName = formData.get("commercialName");
    const manufacturerReference = formData.get("manufacturerReference");
    const supplierReference = formData.get("supplierReference");
    const catalogPriceCents = priceToCents(formData.get("catalogPrice"));
    if (
      typeof brand !== "string" ||
      typeof commercialName !== "string" ||
      typeof manufacturerReference !== "string" ||
      typeof supplierReference !== "string" ||
      catalogPriceCents === null
    ) {
      return;
    }
    updateCatalogMutation.mutate({
      productId: editingOffer.productId,
      offerId: editingOffer.offerId,
      brand,
      commercialName,
      manufacturerReference,
      keywords,
      supplierReference,
      catalogPriceCents,
    });
  };

  return (
    <main className="min-h-full bg-slate-100 p-4 text-slate-900">
      <div className="mx-auto max-w-7xl space-y-4">
        <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] text-blue-700 uppercase">
                Approvisionnement
              </p>
              <h1 className="mt-1 text-2xl font-semibold">
                Commandes du cabinet
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Cherchez une référence, complétez la liste commune, puis
                enregistrez les commandes par revendeur.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
                onClick={openProductForm}
                disabled={
                  cartMutation.isPending ||
                  createProductMutation.isPending ||
                  checkoutMutation.isPending
                }
              >
                Ajouter une référence
              </button>
              {isAdmin ? (
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setIsBrandManagementOpen(true)}
                >
                  Gérer les marques
                </button>
              ) : null}
              <button
                type="button"
                className="rounded-lg border border-blue-700 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
                onClick={() => {
                  setMegadentalImportMessage(null);
                  setMegadentalImportLines([]);
                  setMegadentalInvoiceNumber("");
                  setMegadentalInvoiceDate(
                    DateTime.now().toFormat("yyyy-LL-dd"),
                  );
                  setMegadentalOrderedAt(DateTime.now().toFormat("yyyy-LL-dd"));
                  setIsMegadentalImportOpen(true);
                }}
              >
                Importer une facture Megadental
              </button>
            </div>
          </div>
          <label className="mt-5 block">
            <span className="sr-only">Rechercher une référence</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Marque, nom, référence fabricant ou revendeur"
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base outline-none ring-blue-600 focus:ring-2"
              autoFocus
            />
          </label>
        </section>

        {mutationError ? <ErrorBox message={mutationError.message} /> : null}
        {productMessage ? <SuccessBox message={productMessage} /> : null}
        {megadentalImportMessage ? (
          <SuccessBox message={megadentalImportMessage} />
        ) : null}
        {isCatalogSearchReady && catalogQuery.error ? (
          <ErrorBox
            message={`Impossible de rechercher le catalogue : ${catalogQuery.error.message}`}
          />
        ) : null}
        {cartQuery.error ? (
          <ErrorBox
            message={`Impossible de charger la liste de courses : ${cartQuery.error.message}`}
          />
        ) : null}
        {ordersQuery.error ? (
          <ErrorBox
            message={`Impossible de charger l’historique : ${ordersQuery.error.message}`}
          />
        ) : null}
        {orderAuthorsQuery.error ? (
          <ErrorBox
            message={`Impossible de charger les auteurs des commandes : ${orderAuthorsQuery.error.message}`}
          />
        ) : null}

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(360px,0.9fr)]">
          <section className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
            <SectionHeading
              title="Références trouvées"
              detail={
                isCatalogSearchReady
                  ? `${catalog.length} résultat${catalog.length > 1 ? "s" : ""}`
                  : "3 caractères minimum"
              }
            />
            {isCatalogSearchReady ? (
              <CatalogTable
                catalog={catalog}
                quantitiesByOffer={quantitiesByOffer}
                isUpdating={
                  cartMutation.isPending ||
                  createProductMutation.isPending ||
                  checkoutMutation.isPending
                }
                isAdmin={isAdmin}
                onChangeQuantity={changeQuantity}
                onPromote={(productId) => promoteMutation.mutate(productId)}
                onEdit={setEditingOffer}
              />
            ) : (
              <p className="p-5 text-sm text-slate-500">
                Saisissez au moins 3 caractères pour rechercher un produit.
              </p>
            )}
            {isCatalogSearchReady &&
            catalogQuery.isSuccess &&
            catalog.length === 0 ? (
              <button
                type="button"
                onClick={openProductForm}
                disabled={
                  cartMutation.isPending ||
                  createProductMutation.isPending ||
                  checkoutMutation.isPending
                }
                className="mx-5 mb-5 text-sm font-semibold text-blue-700 hover:underline disabled:opacity-50"
              >
                Référence introuvable ? Ajouter une référence
              </button>
            ) : null}
          </section>

          <section className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
            <SectionHeading
              title="Liste de courses actuelle"
              detail={`${cart.length} référence${cart.length > 1 ? "s" : ""}`}
            />
            <CartTable
              cart={cart}
              isUpdating={
                cartMutation.isPending ||
                createProductMutation.isPending ||
                checkoutMutation.isPending
              }
              onChangeQuantity={changeQuantity}
            />
            <div className="space-y-3 border-t border-slate-200 p-4">
              <p role="status" className="text-sm text-slate-600">
                Liste commune au cabinet —{" "}
                {cartMutation.isPending ||
                (createProductMutation.isPending &&
                  createProductMutation.variables?.cart)
                  ? "enregistrement en cours..."
                  : cartMutation.isError
                    ? "échec de la dernière modification. Veuillez réessayer."
                    : cartQuery.isPending
                      ? "chargement..."
                      : cartQuery.isError
                        ? "chargement impossible."
                        : "enregistrée automatiquement."}
              </p>
              <TextExportActions
                text={formatCartText(cart)}
                filename="liste-de-courses.txt"
                disabled={
                  cartMutation.isPending ||
                  createProductMutation.isPending ||
                  checkoutMutation.isPending ||
                  cartQuery.isError
                }
              />
              <p className="text-sm text-slate-600">
                Les références seront séparées automatiquement en une commande
                par revendeur et la liste sera vidée. Vous devrez transmettre
                vous-même les commandes aux revendeurs.
              </p>
              <button
                type="button"
                disabled={
                  cart.length === 0 ||
                  cartMutation.isPending ||
                  createProductMutation.isPending ||
                  checkoutMutation.isPending ||
                  cartQuery.isError
                }
                onClick={() => checkoutMutation.mutate()}
                className="mt-3 w-full rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {checkoutMutation.isPending
                  ? "Création des commandes..."
                  : "Valider la liste de courses"}
              </button>
            </div>
          </section>
        </div>

        <section className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div>
              <h2 className="font-semibold">Historique des commandes</h2>
              <p className="text-sm text-slate-500">
                Commandes de votre organisation, avec leurs réceptions et prix
                réellement payés.
              </p>
            </div>
            <select
              aria-label="Filtrer les commandes par utilisateur"
              value={authorId}
              onChange={(event) => setAuthorId(event.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Tous les utilisateurs</option>
              {authors.map((author) => (
                <option key={author.id} value={author.id}>
                  {author.fullname}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-0 lg:grid-cols-[minmax(0,0.9fr)_minmax(400px,1.1fr)]">
            <div>
              {ordersQuery.isPending ? (
                <p className="p-5 text-sm text-slate-500">
                  Chargement de l’historique...
                </p>
              ) : ordersQuery.data ? (
                <OrderList
                  orders={orders}
                  selectedOrderId={selectedOrderId}
                  onSelect={setSelectedOrderId}
                />
              ) : null}
              {ordersQuery.hasNextPage ? (
                <div className="p-4">
                  <button
                    type="button"
                    disabled={ordersQuery.isFetching}
                    onClick={() => void ordersQuery.fetchNextPage()}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {ordersQuery.isFetchingNextPage
                      ? "Chargement..."
                      : "Charger les commandes précédentes"}
                  </button>
                </div>
              ) : null}
            </div>
            <div className="border-t border-slate-200 p-5 lg:border-t-0 lg:border-l">
              {selectedOrderQuery.isLoading ? (
                <p className="text-sm text-slate-500">
                  Chargement de la commande...
                </p>
              ) : null}
              {selectedOrderQuery.error ? (
                <ErrorBox
                  message={`Impossible de charger cette commande : ${selectedOrderQuery.error.message}`}
                />
              ) : null}
              {!selectedOrderId ? (
                <p className="text-sm text-slate-500">
                  Sélectionnez une commande pour voir ses lignes et enregistrer
                  une réception.
                </p>
              ) : null}
              {selectedOrderQuery.data ? (
                <OrderDetailPanel
                  order={selectedOrderQuery.data}
                  isSaving={receiptMutation.isPending}
                  onSubmitReceipt={(lines) =>
                    receiptMutation.mutate({
                      orderId: selectedOrderQuery.data.order.id,
                      lines,
                    })
                  }
                />
              ) : null}
            </div>
          </div>
        </section>
      </div>

      {isProductFormOpen ? (
        <ProductForm
          suppliers={suppliersQuery.data ?? []}
          isLoadingSuppliers={suppliersQuery.isPending}
          supplierError={suppliersQuery.error}
          isAdmin={isAdmin}
          isSaving={createProductMutation.isPending}
          error={createProductMutation.error}
          onClose={() => setIsProductFormOpen(false)}
          onSubmit={submitProduct}
        />
      ) : null}
      {editingOffer ? (
        <CatalogEditForm
          offer={editingOffer}
          isSaving={updateCatalogMutation.isPending}
          onClose={() => setEditingOffer(null)}
          onSubmit={submitCatalogEdit}
        />
      ) : null}
      {isMegadentalImportOpen ? (
        <MegadentalImportDialog
          lines={megadentalImportLines}
          isPreviewing={previewMegadentalMutation.isPending}
          isSaving={importMegadentalMutation.isPending}
          invoiceNumber={megadentalInvoiceNumber}
          invoiceDate={megadentalInvoiceDate}
          orderedAt={megadentalOrderedAt}
          onClose={() => setIsMegadentalImportOpen(false)}
          onPreview={(file) => previewMegadentalMutation.mutate(file)}
          onInvoiceNumberChange={setMegadentalInvoiceNumber}
          onInvoiceDateChange={setMegadentalInvoiceDate}
          onOrderedAtChange={setMegadentalOrderedAt}
          onUpdateLine={updateMegadentalImportLine}
          onUpdateKeywords={updateMegadentalImportKeywords}
          onPersistBrand={persistCatalogBrand}
          onPersistKeyword={persistCatalogKeyword}
          onImport={(input) => importMegadentalMutation.mutate(input)}
          isPersistingCatalogValue={
            createCatalogKeywordMutation.isPending ||
            createCatalogBrandMutation.isPending
          }
        />
      ) : null}
      {isBrandManagementOpen ? (
        <BrandManagementDialog
          onClose={() => setIsBrandManagementOpen(false)}
        />
      ) : null}
    </main>
  );
};

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
      {message}
    </div>
  );
}

function SuccessBox({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
      {message}
    </div>
  );
}

function SectionHeading({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex items-baseline justify-between border-b border-slate-200 px-5 py-3">
      <h2 className="font-semibold">{title}</h2>
      <span className="text-sm text-slate-500">{detail}</span>
    </div>
  );
}

function QuantityButtons({
  quantity,
  disabled,
  onChange,
}: {
  quantity: number;
  disabled: boolean;
  onChange: (delta: number) => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <button
        type="button"
        disabled={disabled || quantity === 0}
        onClick={() => onChange(-1)}
        className="grid size-7 place-items-center rounded border border-slate-300 font-semibold hover:bg-slate-100 disabled:opacity-40"
      >
        −
      </button>
      <span className="min-w-7 text-center text-sm font-semibold">
        {quantity}
      </span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(1)}
        className="grid size-7 place-items-center rounded border border-slate-300 font-semibold hover:bg-slate-100 disabled:opacity-40"
      >
        +
      </button>
    </div>
  );
}

function CatalogTable({
  catalog,
  quantitiesByOffer,
  isUpdating,
  isAdmin,
  onChangeQuantity,
  onPromote,
  onEdit,
}: {
  catalog: CatalogOffer[];
  quantitiesByOffer: Map<string, number>;
  isUpdating: boolean;
  isAdmin: boolean;
  onChangeQuantity: (
    offerId: string,
    currentQuantity: number,
    delta: number,
  ) => void;
  onPromote: (productId: string) => void;
  onEdit: (offer: CatalogOffer) => void;
}) {
  const shownPromotionProducts = new Set<string>();
  if (catalog.length === 0)
    return (
      <p className="p-5 text-sm text-slate-500">Aucune référence trouvée.</p>
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead className="bg-slate-50 text-xs tracking-wide text-slate-500 uppercase">
          <tr>
            <th className="px-4 py-3">Produit</th>
            <th className="px-4 py-3">Fabricant</th>
            <th className="px-4 py-3">Revendeur</th>
            <th className="px-4 py-3 text-right">Prix catalogue</th>
            <th className="px-4 py-3 text-right">Quantité</th>
            {isAdmin ? <th className="px-4 py-3" /> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {catalog.map((offer) => {
            const quantity = quantitiesByOffer.get(offer.offerId) ?? 0;
            const canPromote =
              isAdmin &&
              offer.catalogStatus === "proposed" &&
              !shownPromotionProducts.has(offer.productId);
            if (canPromote) shownPromotionProducts.add(offer.productId);
            return (
              <tr key={offer.offerId}>
                <td className="px-4 py-3">
                  <p className="font-medium">
                    {formatProductLabel(offer.brand, offer.commercialName)}
                  </p>
                  {offer.keywords.length > 0 ? (
                    <p className="mt-1 text-xs text-slate-500">
                      {offer.keywords.join(" · ")}
                    </p>
                  ) : null}
                  {offer.catalogStatus === "proposed" ? (
                    <span className="text-xs text-amber-700">
                      Proposition utilisateur
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {offer.manufacturerReference}
                </td>
                <td className="px-4 py-3">
                  <p>{offer.supplierName}</p>
                  <p className="text-xs text-slate-500">
                    {offer.supplierReference}
                  </p>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatPrice(offer.catalogPriceCents)}
                </td>
                <td className="px-4 py-3">
                  <QuantityButtons
                    quantity={quantity}
                    disabled={isUpdating}
                    onChange={(delta) =>
                      onChangeQuantity(offer.offerId, quantity, delta)
                    }
                  />
                </td>
                {isAdmin ? (
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(offer)}
                        className="text-xs font-semibold text-blue-700 hover:underline"
                      >
                        Modifier
                      </button>
                      {canPromote ? (
                        <button
                          type="button"
                          onClick={() => onPromote(offer.productId)}
                          className="text-xs font-semibold text-emerald-700 hover:underline"
                        >
                          Promouvoir
                        </button>
                      ) : null}
                    </div>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CartTable({
  cart,
  isUpdating,
  onChangeQuantity,
}: {
  cart: CartLine[];
  isUpdating: boolean;
  onChangeQuantity: (
    offerId: string,
    currentQuantity: number,
    delta: number,
  ) => void;
}) {
  if (cart.length === 0)
    return (
      <p className="p-5 text-sm text-slate-500">
        Ajoutez des références depuis les résultats de recherche.
      </p>
    );
  return (
    <div className="divide-y divide-slate-100">
      {cart.map((line) => (
        <div key={line.offerId} className="flex items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {formatProductLabel(line.brand, line.commercialName)}
            </p>
            <p className="truncate text-xs text-slate-500">
              {line.supplierName} · {line.supplierReference} ·{" "}
              {formatPrice(line.catalogPriceCents)}
            </p>
          </div>
          <QuantityButtons
            quantity={line.quantity}
            disabled={isUpdating}
            onChange={(delta) =>
              onChangeQuantity(line.offerId, line.quantity, delta)
            }
          />
        </div>
      ))}
    </div>
  );
}

function OrderList({
  orders,
  selectedOrderId,
  onSelect,
}: {
  orders: {
    id: string;
    supplierName: string;
    createdByFullname: string;
    orderedAt: string;
    status: OrderStatus;
    receivedQuantity: number;
    orderedQuantity: number;
    paidTotalCents: number;
    invoicedTotalCents: number;
  }[];
  selectedOrderId: string | null;
  onSelect: (orderId: string) => void;
}) {
  if (orders.length === 0)
    return (
      <p className="p-5 text-sm text-slate-500">
        Aucune commande dans cet historique.
      </p>
    );
  return (
    <div className="divide-y divide-slate-100">
      {orders.map((order) => (
        <button
          type="button"
          key={order.id}
          onClick={() => onSelect(order.id)}
          className={`w-full px-5 py-4 text-left hover:bg-slate-50 ${order.id === selectedOrderId ? "bg-blue-50" : ""}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold">{order.supplierName}</p>
              <p className="mt-1 text-sm text-slate-600">
                {order.createdByFullname} · {formatDate(order.orderedAt)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {orderStatusLabels[order.status]} · {order.receivedQuantity}/
                {order.orderedQuantity} unités reçues
              </p>
            </div>
            <p className="shrink-0 text-sm font-medium">
              {formatPrice(order.invoicedTotalCents || order.paidTotalCents)}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}

function OrderDetailPanel({
  order,
  isSaving,
  onSubmitReceipt,
}: {
  order: OrderDetail;
  isSaving: boolean;
  onSubmitReceipt: (
    lines: {
      orderLineId: string;
      receivedQuantity: number;
      paidUnitPriceCents: number;
    }[],
  ) => void;
}) {
  const submitReceipt = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const lines = order.lines.flatMap((line) => {
      const receivedQuantity = Number(formData.get(`quantity-${line.id}`));
      const paidUnitPriceCents = priceToCents(formData.get(`price-${line.id}`));
      if (
        !Number.isInteger(receivedQuantity) ||
        receivedQuantity <= 0 ||
        paidUnitPriceCents === null
      )
        return [];
      return [{ orderLineId: line.id, receivedQuantity, paidUnitPriceCents }];
    });
    if (lines.length > 0) onSubmitReceipt(lines);
  };
  const canReceive = order.order.status !== "received";
  return (
    <div className="space-y-5" key={order.order.id}>
      <div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
              {order.order.supplierName}
            </p>
            <h2 className="mt-1 text-lg font-semibold">
              Commande du {formatDate(order.order.orderedAt)}
            </h2>
            <p className="text-sm text-slate-600">
              Passée par {order.order.createdByFullname} ·{" "}
              {orderStatusLabels[order.order.status]}
            </p>
          </div>
          <p className="font-semibold">
            {formatPrice(
              order.order.invoicedTotalCents || order.order.paidTotalCents,
            )}
          </p>
        </div>
      </div>
      <TextExportActions
        text={formatOrderText(order)}
        filename={`commande-${order.order.id}.txt`}
      />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[610px] text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr>
              <th className="pb-2">Référence</th>
              <th className="pb-2 text-right">Commandé</th>
              <th className="pb-2 text-right">Reçu</th>
              <th className="pb-2 text-right">Prix catalogue</th>
              <th className="pb-2 text-right">Coût facturé</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {order.lines.map((line) => (
              <tr key={line.id}>
                <td className="py-2">
                  <p className="font-medium">
                    {formatProductLabel(line.brand, line.commercialName)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {[line.manufacturerReference, line.supplierReference]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </td>
                <td className="py-2 text-right">{line.orderedQuantity}</td>
                <td className="py-2 text-right">{line.receivedQuantity}</td>
                <td className="py-2 text-right">
                  {formatPrice(line.catalogUnitPriceCents)}
                </td>
                <td className="py-2 text-right">
                  {formatPrice(line.invoicedTotalCents || line.paidTotalCents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canReceive ? (
        <form
          className="space-y-3 border-t border-slate-200 pt-4"
          onSubmit={submitReceipt}
        >
          <div>
            <h3 className="font-semibold">Enregistrer une réception</h3>
            <p className="text-sm text-slate-500">
              Indiquez les lignes effectivement reçues et leur prix unitaire
              payé.
            </p>
          </div>
          {order.lines.map((line) => {
            const remaining = line.orderedQuantity - line.receivedQuantity;
            if (remaining === 0) return null;
            return (
              <div
                key={line.id}
                className="grid gap-2 rounded-lg bg-slate-50 p-3 sm:grid-cols-[minmax(0,1fr)_100px_130px]"
              >
                <label className="text-sm">
                  <span className="block font-medium">
                    {formatProductLabel(line.brand, line.commercialName)}
                  </span>
                  <span className="text-xs text-slate-500">
                    Reste à recevoir : {remaining}
                  </span>
                </label>
                <input
                  name={`quantity-${line.id}`}
                  type="number"
                  min="0"
                  max={remaining}
                  defaultValue="0"
                  className="rounded border border-slate-300 px-2 py-1.5 text-sm"
                  aria-label={`Quantité reçue pour ${line.commercialName}`}
                />
                <input
                  name={`price-${line.id}`}
                  inputMode="decimal"
                  defaultValue={(line.catalogUnitPriceCents / 100).toFixed(2)}
                  className="rounded border border-slate-300 px-2 py-1.5 text-sm"
                  aria-label={`Prix payé pour ${line.commercialName}`}
                />
              </div>
            );
          })}
          <button
            type="submit"
            disabled={isSaving}
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {isSaving ? "Enregistrement..." : "Enregistrer la réception"}
          </button>
        </form>
      ) : (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Cette commande a été entièrement réceptionnée.
        </p>
      )}
      <div className="border-t border-slate-200 pt-4">
        <h3 className="font-semibold">Réceptions</h3>
        {order.receipts.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">
            Aucune réception enregistrée.
          </p>
        ) : (
          <ol className="mt-3 space-y-3">
            {order.receipts.map((receipt) => (
              <li
                key={receipt.id}
                className="rounded-lg bg-slate-50 p-3 text-sm"
              >
                <p className="font-medium">
                  {formatDate(receipt.receivedAt)} ·{" "}
                  {receipt.receivedByFullname}
                </p>
                {receipt.lines.map((line) => (
                  <p key={line.orderLineId} className="mt-1 text-slate-600">
                    {line.receivedQuantity} unité
                    {line.receivedQuantity > 1 ? "s" : ""} ·{" "}
                    {formatPrice(line.paidUnitPriceCents)} / unité
                  </p>
                ))}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function BrandManagementDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const brandsQuery = useQuery({
    queryKey: ["ordering-catalog-brands"],
    queryFn: orderingApi.listBrands,
  });
  const conflictsQuery = useQuery({
    queryKey: ["ordering-catalog-brand-conflicts"],
    queryFn: orderingApi.listBrandNormalizationConflicts,
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["ordering-catalog-brands"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["ordering-catalog-brand-conflicts"],
    });
    await queryClient.invalidateQueries({ queryKey: ["ordering-catalog"] });
  };
  const createBrandMutation = useMutation({
    mutationFn: orderingApi.createBrand,
    onSuccess: refresh,
  });
  const updateBrandMutation = useMutation({
    mutationFn: ({ brandId, name }: { brandId: string; name: string }) =>
      orderingApi.updateBrand(brandId, name),
    onSuccess: refresh,
  });
  const createAliasMutation = useMutation({
    mutationFn: ({ brandId, name }: { brandId: string; name: string }) =>
      orderingApi.createBrandAlias(brandId, name),
    onSuccess: refresh,
  });
  const deleteAliasMutation = useMutation({
    mutationFn: ({
      brandId,
      aliasName,
    }: {
      brandId: string;
      aliasName: string;
    }) => orderingApi.deleteBrandAlias(brandId, aliasName),
    onSuccess: refresh,
  });
  const mergeProductsMutation = useMutation({
    mutationFn: ({
      targetProductId,
      sourceProductId,
    }: {
      targetProductId: string;
      sourceProductId: string;
    }) => orderingApi.mergeCatalogProducts(targetProductId, sourceProductId),
    onSuccess: refresh,
  });
  const mutationError =
    createBrandMutation.error ??
    updateBrandMutation.error ??
    createAliasMutation.error ??
    deleteAliasMutation.error ??
    mergeProductsMutation.error;
  const isSaving =
    createBrandMutation.isPending ||
    updateBrandMutation.isPending ||
    createAliasMutation.isPending ||
    deleteAliasMutation.isPending ||
    mergeProductsMutation.isPending;

  const submitBrand = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = new FormData(event.currentTarget).get("name");
    if (typeof name === "string" && name.trim()) {
      createBrandMutation.mutate(name);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/40 p-4">
      <section className="mx-auto my-8 max-w-4xl rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] text-blue-700 uppercase">
              Catalogue
            </p>
            <h2 className="mt-1 text-xl font-semibold">Marques et alias</h2>
            <p className="mt-1 text-sm text-slate-600">
              Les alias sont normalisés côté serveur lors des créations,
              modifications et imports.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Fermer
          </button>
        </div>

        {mutationError ? <ErrorBox message={mutationError.message} /> : null}
        {brandsQuery.error ? (
          <ErrorBox
            message={`Impossible de charger les marques : ${brandsQuery.error.message}`}
          />
        ) : null}

        <form className="mt-5 flex gap-2" onSubmit={submitBrand}>
          <label className="sr-only" htmlFor="new-catalog-brand">
            Nouvelle marque
          </label>
          <input
            id="new-catalog-brand"
            name="name"
            required
            disabled={isSaving}
            placeholder="Nouvelle marque canonique"
            className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none ring-blue-600 focus:ring-2"
          />
          <button
            type="submit"
            disabled={isSaving}
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
          >
            Ajouter
          </button>
        </form>

        <div className="mt-5 space-y-3">
          {(brandsQuery.data ?? []).map((brand) => (
            <article
              key={brand.id}
              className="rounded-xl border border-slate-200 p-4"
            >
              <form
                className="flex flex-wrap gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  const name = new FormData(event.currentTarget).get("name");
                  if (typeof name === "string" && name.trim()) {
                    updateBrandMutation.mutate({ brandId: brand.id, name });
                  }
                }}
              >
                <input
                  name="name"
                  defaultValue={brand.name}
                  required
                  disabled={isSaving}
                  className="min-w-48 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none ring-blue-600 focus:ring-2"
                />
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Renommer
                </button>
              </form>
              <div className="mt-3 flex flex-wrap gap-2">
                {brand.aliases.map((alias) => (
                  <span
                    key={alias}
                    className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700"
                  >
                    {alias}
                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={() =>
                        deleteAliasMutation.mutate({
                          brandId: brand.id,
                          aliasName: alias,
                        })
                      }
                      className="ml-1 text-slate-500 hover:text-red-700 disabled:opacity-50"
                      aria-label={`Retirer l'alias ${alias}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <form
                className="mt-3 flex flex-wrap gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  const name = new FormData(event.currentTarget).get("name");
                  if (typeof name === "string" && name.trim()) {
                    createAliasMutation.mutate({ brandId: brand.id, name });
                  }
                }}
              >
                <input
                  name="name"
                  required
                  disabled={isSaving}
                  placeholder="Ajouter un alias"
                  className="min-w-48 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none ring-blue-600 focus:ring-2"
                />
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-lg border border-blue-700 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                >
                  Ajouter l'alias
                </button>
              </form>
            </article>
          ))}
        </div>

        <section className="mt-6 border-t border-slate-200 pt-5">
          <h3 className="text-base font-semibold">Conflits à vérifier</h3>
          <p className="mt-1 text-sm text-slate-600">
            Ces doublons potentiels n'ont pas été fusionnés automatiquement.
          </p>
          {conflictsQuery.error ? (
            <ErrorBox
              message={`Impossible de charger les conflits : ${conflictsQuery.error.message}`}
            />
          ) : null}
          {(conflictsQuery.data ?? []).length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">Aucun conflit ouvert.</p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm">
              {(conflictsQuery.data ?? []).map((conflict) => (
                <li
                  key={conflict.id}
                  className="rounded-lg bg-amber-50 p-3 text-amber-950"
                >
                  <p className="font-semibold">
                    {conflict.canonicalBrandName} · réf. fabricant{" "}
                    {conflict.manufacturerReference}
                  </p>
                  <p className="mt-1">
                    {formatProductLabel(
                      conflict.firstProduct.brand,
                      conflict.firstProduct.commercialName,
                    )}
                    {" / "}
                    {formatProductLabel(
                      conflict.secondProduct.brand,
                      conflict.secondProduct.commercialName,
                    )}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={() =>
                        mergeProductsMutation.mutate({
                          targetProductId: conflict.firstProduct.id,
                          sourceProductId: conflict.secondProduct.id,
                        })
                      }
                      className="rounded border border-amber-700 px-2 py-1 text-xs font-semibold hover:bg-amber-100 disabled:opacity-50"
                    >
                      Conserver le premier
                    </button>
                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={() =>
                        mergeProductsMutation.mutate({
                          targetProductId: conflict.secondProduct.id,
                          sourceProductId: conflict.firstProduct.id,
                        })
                      }
                      className="rounded border border-amber-700 px-2 py-1 text-xs font-semibold hover:bg-amber-100 disabled:opacity-50"
                    >
                      Conserver le second
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </section>
    </div>
  );
}

function BrandInput({
  value,
  disabled,
  onChange,
  onPersist,
}: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onPersist?: (name: string) => Promise<string>;
}) {
  const [lastPersistedName, setLastPersistedName] = useState<string | null>(
    null,
  );
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const deferredValue = useDeferredValue(value);
  const brandsQuery = useQuery({
    queryKey: ["ordering-catalog-brand-suggestions", deferredValue],
    queryFn: () => orderingApi.listBrandSuggestions(deferredValue),
    enabled: deferredValue.trim().length > 0,
  });
  const persist = () => {
    const name = value.trim();
    if (!onPersist || !name || name === lastPersistedName) return;
    setLastPersistedName(name);
    void onPersist(name)
      .then((persistedName) => {
        setLastPersistedName(persistedName);
        onChange(persistedName);
      })
      .catch(() => setLastPersistedName(null));
  };
  const selectSuggestion = (name: string) => {
    setLastPersistedName(name);
    setIsSuggestionsOpen(false);
    setActiveSuggestionIndex(0);
    onChange(name);
  };

  return (
    <>
      <input
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value);
          setActiveSuggestionIndex(0);
          setIsSuggestionsOpen(true);
        }}
        onFocus={() => {
          setActiveSuggestionIndex(0);
          setIsSuggestionsOpen(true);
        }}
        onBlur={() => {
          setIsSuggestionsOpen(false);
          persist();
        }}
        onKeyDown={(event) => {
          const suggestions = brandsQuery.data ?? [];
          if (event.key === "ArrowDown" && suggestions.length > 0) {
            event.preventDefault();
            setIsSuggestionsOpen(true);
            setActiveSuggestionIndex((index) =>
              Math.min(index + 1, suggestions.length - 1),
            );
          } else if (event.key === "ArrowUp" && suggestions.length > 0) {
            event.preventDefault();
            setActiveSuggestionIndex((index) => Math.max(index - 1, 0));
          } else if (
            (event.key === "Enter" || event.key === "Tab") &&
            isSuggestionsOpen &&
            suggestions[activeSuggestionIndex]
          ) {
            event.preventDefault();
            selectSuggestion(suggestions[activeSuggestionIndex].name);
          } else if (event.key === "Enter") {
            event.preventDefault();
            persist();
          } else if (event.key === "Escape") {
            setIsSuggestionsOpen(false);
          }
        }}
        placeholder="Facultatif"
        className="w-36 rounded border border-slate-300 px-2 py-1.5"
      />
      {isSuggestionsOpen &&
      value.trim() &&
      (brandsQuery.data ?? []).length > 0 ? (
        <div className="mt-1 rounded-lg border border-slate-200 bg-white text-sm shadow-sm">
          {(brandsQuery.data ?? []).map((suggestion, index) => (
            <button
              type="button"
              key={suggestion.name}
              disabled={disabled}
              onMouseDown={(event) => {
                event.preventDefault();
                selectSuggestion(suggestion.name);
              }}
              className={`block w-full px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-50 ${
                index === activeSuggestionIndex
                  ? "bg-slate-200 font-medium text-slate-950"
                  : ""
              }`}
            >
              {suggestion.name}
            </button>
          ))}
        </div>
      ) : null}
      {brandsQuery.error ? (
        <p className="mt-1 text-xs text-red-700">
          Impossible de charger les marques existantes.
        </p>
      ) : null}
    </>
  );
}

function KeywordInput({
  values,
  disabled,
  onChange,
  onCreate,
}: {
  values: string[];
  disabled: boolean;
  onChange: (values: string[]) => void;
  onCreate?: (name: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState("");
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const deferredDraft = useDeferredValue(draft);
  const keywordQuery = useQuery({
    queryKey: ["ordering-catalog-keywords", deferredDraft],
    queryFn: () => orderingApi.listKeywords(deferredDraft),
    enabled: deferredDraft.trim().length > 0,
  });
  const keywords = keywordQuery.data ?? [];
  const suggestions = keywords.filter(
    (keyword) =>
      !values.some(
        (value) =>
          value.toLocaleLowerCase() === keyword.name.toLocaleLowerCase(),
      ),
  );

  const addNames = (rawNames: string[]) => {
    let next = values;
    const added: string[] = [];
    for (const rawName of rawNames) {
      const name = rawName.trim().replaceAll(/\s+/g, " ");
      if (
        name.length === 0 ||
        next.some(
          (value) => value.toLocaleLowerCase() === name.toLocaleLowerCase(),
        )
      ) {
        continue;
      }
      next = [...next, name];
      added.push(name);
    }
    if (next !== values) onChange(next);
    for (const name of added) {
      const isKnown = keywords.some(
        (keyword) =>
          keyword.name.toLocaleLowerCase() === name.toLocaleLowerCase(),
      );
      if (!isKnown) void onCreate?.(name).catch(() => undefined);
    }
  };

  const commitDraft = () => {
    if (draft.trim()) {
      addNames([draft]);
    }
    setDraft("");
  };
  const selectSuggestion = (name: string) => {
    addNames([name]);
    setActiveSuggestionIndex(0);
    setDraft("");
  };

  return (
    <div>
      <p className="text-sm font-medium text-slate-700">
        Mots-clés{" "}
        <span className="text-xs font-normal text-slate-400">
          séparés par des virgules
        </span>
      </p>
      <div className="mt-1 flex flex-wrap gap-2 rounded-lg border border-slate-300 p-2">
        {values.map((keyword) => (
          <span
            key={keyword}
            className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-800"
          >
            {keyword}
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                onChange(values.filter((value) => value !== keyword))
              }
              className="text-blue-700 hover:text-blue-950 disabled:opacity-50"
              aria-label={`Retirer le mot-clé ${keyword}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={draft}
          disabled={disabled}
          onChange={(event) => {
            const value = event.target.value;
            if (value.includes(",")) {
              const segments = value.split(",");
              addNames(segments.slice(0, -1));
              setDraft(segments[segments.length - 1]);
            } else {
              setDraft(value);
            }
            setActiveSuggestionIndex(0);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" && suggestions.length > 0) {
              event.preventDefault();
              setActiveSuggestionIndex((index) =>
                Math.min(index + 1, suggestions.length - 1),
              );
            } else if (event.key === "ArrowUp" && suggestions.length > 0) {
              event.preventDefault();
              setActiveSuggestionIndex((index) => Math.max(index - 1, 0));
            } else if (
              (event.key === "Enter" || event.key === "Tab") &&
              suggestions[activeSuggestionIndex]
            ) {
              event.preventDefault();
              selectSuggestion(suggestions[activeSuggestionIndex].name);
            } else if (event.key === "Enter" && draft.trim()) {
              event.preventDefault();
              commitDraft();
            }
          }}
          onBlur={commitDraft}
          placeholder="Ajouter un mot-clé"
          className="min-w-40 flex-1 border-0 px-1 py-1 text-sm outline-none"
        />
      </div>
      {draft.trim() && suggestions.length > 0 ? (
        <div className="mt-1 rounded-lg border border-slate-200 bg-white text-sm shadow-sm">
          {suggestions.map((keyword, index) => (
            <button
              type="button"
              key={keyword.name}
              disabled={disabled}
              onMouseDown={(event) => {
                event.preventDefault();
                selectSuggestion(keyword.name);
              }}
              className={`block w-full px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-50 ${
                index === activeSuggestionIndex
                  ? "bg-slate-200 font-medium text-slate-950"
                  : ""
              }`}
            >
              {keyword.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ProductForm({
  suppliers,
  isLoadingSuppliers,
  supplierError,
  isAdmin,
  isSaving,
  error,
  onClose,
  onSubmit,
}: {
  suppliers: { id: string; name: string }[];
  isLoadingSuppliers: boolean;
  supplierError: Error | null;
  isAdmin: boolean;
  isSaving: boolean;
  error: Error | null;
  onClose: () => void;
  onSubmit: (product: CreateProductInput) => void;
}) {
  const [offerCount, setOfferCount] = useState(1);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [addToCart, setAddToCart] = useState(true);
  const [cartOfferIndex, setCartOfferIndex] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving) return;
    setFormError(null);
    const formData = new FormData(event.currentTarget);
    const brand = formData.get("brand");
    const commercialName = formData.get("commercialName");
    const manufacturerReference = formData.get("manufacturerReference");
    if (
      typeof brand !== "string" ||
      typeof commercialName !== "string" ||
      typeof manufacturerReference !== "string"
    )
      return;
    const offers = Array.from({ length: offerCount }).flatMap((_, index) => {
      const supplierId = formData.get(`supplierId-${index}`);
      const supplierReference = formData.get(`supplierReference-${index}`);
      const price = formData.get(`catalogPrice-${index}`);
      const catalogPriceCents =
        typeof price === "string" && price.trim() === ""
          ? 0
          : priceToCents(price);
      if (
        typeof supplierId !== "string" ||
        typeof supplierReference !== "string" ||
        catalogPriceCents === null
      )
        return [];
      return [
        {
          supplierId,
          supplierReference: supplierReference.trim(),
          catalogPriceCents,
        },
      ];
    });
    if (offers.length !== offerCount) {
      setFormError(
        "Vérifiez les fournisseurs, leurs références et les prix saisis.",
      );
      return;
    }
    const quantity = Number(formData.get("quantity"));
    if (
      addToCart &&
      (!Number.isInteger(quantity) || quantity <= 0 || quantity > 2147483647)
    )
      return;
    onSubmit({
      brand: brand.trim(),
      commercialName: commercialName.trim(),
      manufacturerReference: manufacturerReference.trim(),
      keywords,
      offers,
      cart: addToCart ? { offerIndex: cartOfferIndex, quantity } : null,
    });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-form-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
        onSubmit={submit}
      >
        <div>
          <h2 id="product-form-title" className="text-xl font-semibold">
            Ajouter une référence
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {isAdmin
              ? "La référence sera ajoutée au catalogue maître."
              : "La référence sera proposée au catalogue et immédiatement commandable."}{" "}
            Si elle existe déjà, elle sera réutilisée sans modifier sa fiche.
          </p>
        </div>
        <fieldset disabled={isSaving} className="mt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1 text-sm">
              <span>Marque</span>
              <input
                name="brand"
                required
                pattern=".*\S.*"
                autoFocus
                placeholder="Marque"
                className="w-full rounded-lg border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span>Descriptif</span>
              <input
                name="commercialName"
                required
                pattern=".*\S.*"
                placeholder="Descriptif"
                className="w-full rounded-lg border border-slate-300 px-3 py-2"
              />
            </label>
          </div>
          <label className="block space-y-1 text-sm">
            <span>Référence fabricant (facultative)</span>
            <input
              name="manufacturerReference"
              placeholder="Référence fabricant (facultative)"
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            />
          </label>
          <KeywordInput
            values={keywords}
            disabled={isSaving}
            onChange={setKeywords}
          />
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Références revendeurs</h3>
              <button
                type="button"
                disabled={isSaving}
                onClick={() => setOfferCount((count) => count + 1)}
                className="text-sm font-semibold text-blue-700 hover:underline"
              >
                Ajouter un revendeur
              </button>
            </div>
            {Array.from({ length: offerCount }).map((_, index) => (
              <div
                key={index}
                className="space-y-2 rounded-lg border border-slate-200 p-3"
              >
                <div className="grid gap-3 sm:grid-cols-[1fr_1fr_130px]">
                  <label className="block space-y-1 text-sm">
                    <span>
                      {index === 0 ? "Fournisseur" : `Fournisseur ${index + 1}`}
                    </span>
                    <select
                      name={`supplierId-${index}`}
                      required
                      className="w-full rounded-lg border border-slate-300 px-3 py-2"
                    >
                      <option value="">Revendeur</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block space-y-1 text-sm">
                    <span>
                      {index === 0
                        ? "Référence fournisseur"
                        : `Référence fournisseur ${index + 1}`}
                    </span>
                    <input
                      name={`supplierReference-${index}`}
                      required
                      pattern=".*\S.*"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </label>
                  <label className="block space-y-1 text-sm">
                    <span>
                      {index === 0
                        ? "Prix catalogue (facultatif)"
                        : `Prix catalogue ${index + 1} (facultatif)`}
                    </span>
                    <input
                      name={`catalogPrice-${index}`}
                      inputMode="decimal"
                      pattern="[0-9]+([,.][0-9]{1,2})?"
                      placeholder="Non renseigné"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </label>
                </div>
                {addToCart && offerCount > 1 ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="cartOfferIndex"
                      value={index}
                      checked={cartOfferIndex === index}
                      onChange={() => setCartOfferIndex(index)}
                    />
                    Commander auprès du fournisseur {index + 1}
                  </label>
                ) : null}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-lg bg-slate-50 p-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={addToCart}
                onChange={(event) => setAddToCart(event.target.checked)}
              />
              Ajouter à la liste de courses
            </label>
            <label className="flex items-center gap-2 text-sm">
              <span>Quantité</span>
              <input
                name="quantity"
                type="number"
                min={1}
                max={2147483647}
                step={1}
                required={addToCart}
                disabled={!addToCart}
                defaultValue={1}
                className="w-24 rounded-lg border border-slate-300 px-3 py-2 disabled:opacity-50"
              />
            </label>
          </div>
          {isLoadingSuppliers ? (
            <p className="text-sm text-slate-500">
              Chargement des fournisseurs...
            </p>
          ) : null}
          {supplierError ? (
            <p role="alert" className="text-sm text-red-700">
              Impossible de charger les fournisseurs : {supplierError.message}
            </p>
          ) : null}
          {!isLoadingSuppliers && !supplierError && suppliers.length === 0 ? (
            <p className="text-sm text-red-700">
              Aucun fournisseur disponible.
            </p>
          ) : null}
          {error || formError ? (
            <p role="alert" className="text-sm text-red-700">
              {formError ?? error?.message}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              disabled={isSaving}
              onClick={onClose}
              className="px-3 py-2 text-sm text-slate-600"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={
                isLoadingSuppliers ||
                Boolean(supplierError) ||
                suppliers.length === 0
              }
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {isSaving
                ? "Enregistrement..."
                : addToCart
                  ? "Créer et ajouter à la liste de courses"
                  : "Créer la référence"}
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  );
}

function CatalogEditForm({
  offer,
  isSaving,
  onClose,
  onSubmit,
}: {
  offer: CatalogOffer;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>, keywords: string[]) => void;
}) {
  const [keywords, setKeywords] = useState(offer.keywords);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <form
        className="w-full max-w-xl space-y-4 rounded-2xl bg-white p-6 shadow-xl"
        onSubmit={(event) => onSubmit(event, keywords)}
      >
        <div>
          <h2 className="text-xl font-semibold">Modifier le catalogue</h2>
          <p className="mt-1 text-sm text-slate-500">{offer.supplierName}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            name="brand"
            defaultValue={offer.brand}
            placeholder="Marque facultative"
            className="rounded-lg border border-slate-300 px-3 py-2"
          />
          <input
            name="commercialName"
            required
            defaultValue={offer.commercialName}
            className="rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <input
          name="manufacturerReference"
          defaultValue={offer.manufacturerReference}
          placeholder="Référence fabricant facultative"
          className="w-full rounded-lg border border-slate-300 px-3 py-2"
        />
        <KeywordInput
          values={keywords}
          disabled={isSaving}
          onChange={setKeywords}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            name="supplierReference"
            required
            defaultValue={offer.supplierReference}
            className="rounded-lg border border-slate-300 px-3 py-2"
          />
          <input
            name="catalogPrice"
            required
            inputMode="decimal"
            defaultValue={(offer.catalogPriceCents / 100).toFixed(2)}
            className="rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            disabled={isSaving}
            onClick={onClose}
            className="px-3 py-2 text-sm text-slate-600"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {isSaving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function MegadentalImportDialog({
  lines,
  isPreviewing,
  isSaving,
  invoiceNumber,
  invoiceDate,
  orderedAt,
  onClose,
  onPreview,
  onInvoiceNumberChange,
  onInvoiceDateChange,
  onOrderedAtChange,
  onUpdateLine,
  onUpdateKeywords,
  onPersistBrand,
  onPersistKeyword,
  onImport,
  isPersistingCatalogValue,
}: {
  lines: MegadentalImportLine[];
  isPreviewing: boolean;
  isSaving: boolean;
  invoiceNumber: string;
  invoiceDate: string;
  orderedAt: string;
  onClose: () => void;
  onPreview: (file: File) => void;
  onInvoiceNumberChange: (value: string) => void;
  onInvoiceDateChange: (value: string) => void;
  onOrderedAtChange: (value: string) => void;
  onUpdateLine: (
    importId: string,
    field:
      | "brand"
      | "commercialName"
      | "manufacturerReference"
      | "deliveredQuantity"
      | "invoicedUnitPriceCents"
      | "invoicedTotalCents",
    value: string,
  ) => void;
  onUpdateKeywords: (importId: string, keywords: string[]) => void;
  onPersistBrand: (name: string) => Promise<string>;
  onPersistKeyword: (name: string) => Promise<void>;
  onImport: (input: {
    invoiceNumber: string;
    invoiceDate: string;
    orderedAt: string;
    lines: MegadentalImportLine[];
  }) => void;
  isPersistingCatalogValue: boolean;
}) {
  const invoicedTotalCents = lines.reduce(
    (total, line) => total + (line.invoicedTotalCents ?? 0),
    0,
  );

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const invoiceDateTime = DateTime.fromFormat(invoiceDate, "yyyy-LL-dd", {
      zone: "utc",
    });
    const orderedDateTime = DateTime.fromFormat(orderedAt, "yyyy-LL-dd", {
      zone: "utc",
    });
    const invoiceDateIso = invoiceDateTime.toISO();
    const orderedAtIso = orderedDateTime.toISO();
    if (
      lines.length > 0 &&
      !isPersistingCatalogValue &&
      invoiceDateIso &&
      orderedAtIso
    ) {
      onImport({
        invoiceNumber,
        invoiceDate: invoiceDateIso,
        orderedAt: orderedAtIso,
        lines,
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <form
        className="flex max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-[1600px] flex-col rounded-2xl bg-white shadow-xl"
        onSubmit={submit}
      >
        <div className="border-b border-slate-200 p-6">
          <h2 className="text-xl font-semibold">
            Importer une facture Megadental
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Vérifiez les lignes de facture. Les nouvelles références seront
            immédiatement utilisables et marquées à vérifier.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <label>
              <span className="text-sm font-medium text-slate-700">
                N° de facture
              </span>
              <input
                required
                value={invoiceNumber}
                disabled={isSaving}
                onChange={(event) => onInvoiceNumberChange(event.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5"
              />
            </label>
            <label>
              <span className="text-sm font-medium text-slate-700">
                Date de facture
              </span>
              <input
                required
                type="date"
                value={invoiceDate}
                disabled={isSaving}
                onChange={(event) => onInvoiceDateChange(event.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5"
              />
            </label>
            <label>
              <span className="text-sm font-medium text-slate-700">
                Date de commande
              </span>
              <input
                required
                type="date"
                value={orderedAt}
                disabled={isSaving}
                onChange={(event) => onOrderedAtChange(event.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5"
              />
            </label>
          </div>
          <label className="mt-4 block">
            <span className="sr-only">Facture Megadental au format PDF</span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              disabled={isPreviewing || isSaving}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                if (file) onPreview(file);
              }}
              className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
            />
          </label>
          {isPreviewing ? (
            <p className="mt-3 text-sm text-slate-600">Analyse du PDF...</p>
          ) : null}
        </div>
        <div className="min-h-0 overflow-auto p-6">
          {lines.length === 0 && !isPreviewing ? (
            <p className="text-sm text-slate-500">
              Sélectionnez une facture PDF pour afficher ses lignes.
            </p>
          ) : null}
          {lines.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1600px] text-left text-sm">
                <thead className="border-b border-slate-200 text-xs tracking-wide text-slate-500 uppercase">
                  <tr>
                    <th className="px-2 py-2">Réf. Megadental</th>
                    <th className="px-2 py-2">Marque</th>
                    <th className="px-2 py-2">Nom commercial</th>
                    <th className="px-2 py-2">Référence fabricant</th>
                    <th className="px-2 py-2">Mots-clés</th>
                    <th className="px-2 py-2 text-right">Qté commandée</th>
                    <th className="px-2 py-2 text-right">Qté livrée</th>
                    <th className="px-2 py-2 text-right">Prix référence TTC</th>
                    <th className="px-2 py-2 text-right">Coût unitaire TTC</th>
                    <th className="px-2 py-2 text-right">Total facturé TTC</th>
                    <th className="px-2 py-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {lines.map((line) => (
                    <tr key={line.importId}>
                      <td className="px-2 py-2 font-mono text-xs">
                        {line.supplierReference}
                      </td>
                      <td className="px-2 py-2">
                        <BrandInput
                          value={line.brand}
                          disabled={isSaving}
                          onChange={(brand) =>
                            onUpdateLine(line.importId, "brand", brand)
                          }
                          onPersist={onPersistBrand}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          required={!line.existing}
                          value={line.commercialName}
                          disabled={isSaving}
                          onChange={(event) =>
                            onUpdateLine(
                              line.importId,
                              "commercialName",
                              event.target.value,
                            )
                          }
                          className="w-full min-w-72 rounded border border-slate-300 px-2 py-1.5"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          value={line.manufacturerReference}
                          disabled={isSaving}
                          onChange={(event) =>
                            onUpdateLine(
                              line.importId,
                              "manufacturerReference",
                              event.target.value,
                            )
                          }
                          placeholder="Facultatif"
                          className="w-44 rounded border border-slate-300 px-2 py-1.5"
                        />
                      </td>
                      <td className="min-w-64 px-2 py-2 align-top">
                        <KeywordInput
                          values={line.keywords}
                          disabled={isSaving}
                          onChange={(keywords) =>
                            onUpdateKeywords(line.importId, keywords)
                          }
                          onCreate={onPersistKeyword}
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="tabular-nums">
                          {line.orderedQuantity}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={line.deliveredQuantity}
                          disabled={isSaving}
                          onChange={(event) =>
                            onUpdateLine(
                              line.importId,
                              "deliveredQuantity",
                              event.target.value,
                            )
                          }
                          className="w-20 rounded border border-slate-300 px-2 py-1.5 text-right tabular-nums"
                        />
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {formatPrice(line.catalogPriceCents)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <input
                          inputMode="decimal"
                          value={
                            line.invoicedUnitPriceCents === null
                              ? ""
                              : (line.invoicedUnitPriceCents / 100).toFixed(2)
                          }
                          disabled={isSaving}
                          onChange={(event) =>
                            onUpdateLine(
                              line.importId,
                              "invoicedUnitPriceCents",
                              event.target.value,
                            )
                          }
                          className="w-24 rounded border border-slate-300 px-2 py-1.5 text-right tabular-nums"
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <input
                          inputMode="decimal"
                          value={
                            line.invoicedTotalCents === null
                              ? ""
                              : (line.invoicedTotalCents / 100).toFixed(2)
                          }
                          disabled={isSaving}
                          onChange={(event) =>
                            onUpdateLine(
                              line.importId,
                              "invoicedTotalCents",
                              event.target.value,
                            )
                          }
                          className="w-24 rounded border border-slate-300 px-2 py-1.5 text-right tabular-nums"
                        />
                      </td>
                      <td className="px-2 py-2">
                        {line.isOutOfStock ? (
                          <span className="text-xs font-medium text-amber-700">
                            En rupture, non facturée
                          </span>
                        ) : line.existing ? (
                          <span className="text-xs font-medium text-amber-700">
                            Référence connue
                          </span>
                        ) : (
                          <span className="text-xs font-medium text-emerald-700">
                            À vérifier
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-200 p-4">
          <p className="mr-auto self-center text-sm font-semibold tabular-nums">
            Total facturé : {formatPrice(invoicedTotalCents)}
          </p>
          <button
            type="button"
            disabled={isSaving}
            onClick={onClose}
            className="px-3 py-2 text-sm text-slate-600"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={
              lines.length === 0 ||
              isPreviewing ||
              isSaving ||
              isPersistingCatalogValue
            }
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {isSaving
              ? "Import en cours..."
              : isPersistingCatalogValue
                ? "Enregistrement..."
                : "Enregistrer la facture"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default OrderingPage;
